/**
 * Request Caching Strategy — cache store + entry helpers
 * Cache frequently accessed data untuk reduce database queries
 * Uses localStorage via OfflineStorage for persistence
 */

import { OfflineStorage } from '../offlineStorage';

export const cache = new OfflineStorage();

// Default cache TTL (5 minutes)
export const DEFAULT_TTL = 5 * 60 * 1000;

/**
 * Set cache entry
 */
export const setCacheEntry = <T>(
    key: string,
    data: T,
    ttl: number = DEFAULT_TTL
): void => {
    cache.set(key, data, ttl);
    if (import.meta.env.DEV) {
        console.log(`💾 Saved to cache: ${key}`);
    }
};

/**
 * Get cache entry if valid
 */
export const getCacheEntry = <T>(key: string): T | null => {
    const entry = cache.get<T>(key);
    
    if (entry !== null && import.meta.env.DEV) {
        console.log(`✅ Cache HIT for ${key}`);
    } else if (import.meta.env.DEV) {
        console.log(`📦 Cache MISS for ${key}`);
    }

    return entry;
};

/**
 * Get stale cache entry (even if expired)
 * Used as fallback when fresh data is unavailable
 */
export const getStaleCacheEntry = <T>(key: string): T | null => {
    try {
        const item = localStorage.getItem('omd_cache_' + key);
        if (!item) return null;
        
        const parsed = JSON.parse(item);
        if (import.meta.env.DEV) {
            console.log(`♻️ Using STALE cache for ${key}`);
        }
        return parsed.data as T;
    } catch (error) {
        return null;
    }
};

/**
 * Clear specific cache entry
 */
export const clearCacheEntry = (key: string): void => {
    cache.remove(key);
};

/**
 * Clear all cache
 */
export const clearAllCache = (): void => {
    cache.clearAll();
};
